/**
 * Share dialog — connections-first, multi-recipient.
 *
 * Header mirrors the Who-has-access dialog ("Share <folder>"). You add
 * recipients from a picker (orgs you're connected to, plus "share with a new
 * organization"); each becomes a card with its own permission. Clicking Share
 * fires it all: connected recipients land instantly (silent re-share), new
 * ones become a connection/share request the recipient is notified about.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Building2, File, Folder, Loader2, Plus, Shield, SquarePen, User, UserPlus, X } from 'lucide-react'
import { useTheme, getThemeClass, useToast, useConfirm, Tooltip, FileIcon, avatarBg, getInitials } from '@papan/core-ui/ui'
import { isMiniappFolder } from '@papan/types'
import { orgConnectionsApi, type ConnectionRow, type OutgoingRequest, type FolderAccessRow } from '../client/index.js'
import { RecipientAccessPicker, type SharePermission } from './SharePermissionPicker.js'
import { ShareToNewOrgDialog } from './ShareToNewOrgDialog.js'
import { pinnedBatch } from '@papan/api-client'

interface Props {
  folderId: string
  folderName: string
  isFolder?: boolean
  folderPath?: string
  /** Caller's own effective permission on the folder — caps each card. */
  myPermission?: string
  /** Reverse-share (step 7): pre-add this connected org as a recipient on
   *  open. Set when the dialog is launched by dragging own content onto a
   *  cross-tenant `.shared` — the drop already chose the recipient, so we seed
   *  its card instead of making the user re-pick it. */
  initialRecipientTenantId?: string
  /** Extra folders to share ALONGSIDE folderId (beyond auto-detected chainRefs) —
   *  the chain-escape "Share the source folders too" flow seeds every escaping
   *  chain's target here, so one dialog shares them all. Shown in the header. */
  alsoShare?: Array<{ id: string; name: string; isFolder: boolean }>
  /** Opens the full "Who has access" dialog for this folder (parent wires it). */
  onOpenWhoHasAccess?: () => void
  /** Whether the caller can MANAGE access (owner/admin). A member can still add
   *  recipients (share to outsiders), but can't re-manage an EXISTING recipient's
   *  access or open Who-has-access — those stay read-only / hidden. Default true
   *  (owner-initiated reverse-share / chain-escape flows). */
  canManage?: boolean
  /** Bumped by the parent when the Who-has-access dialog (opened from here)
   *  closes, so the recipient cards re-fetch their permission + re-sharing
   *  state. Staged new recipients are preserved across the reload. */
  refreshKey?: number
  onClose: () => void
}

/**
 * (#327 B7) Does this share hand over an app's SOURCE? True for a real
 * `.miniapp` folder. Name + isFolder, matching how every other package
 * detector gates — a FILE called `x.miniapp` is not a package, and a mount
 * pointer is never the thing being shared from here (you share your own
 * folder, not someone else's mount of it).
 */
function sharesPackageSource(name: string, isFolder: boolean): boolean {
  return isFolder && isMiniappFolder(name)
}

/** An outgoing request still in the handshake (not accepted, not closed). */
const isInFlight = (r: OutgoingRequest) =>
  r.completed == null &&
  (r.state === 'pending_recipient' || r.state === 'pending_initiator_confirm')

/** Map a grant's stored permission to the Share dialog's lattice. */
const toSharePerm = (p: string): SharePermission =>
  (p === 'readcopy' || p === 'readwrite' || p === 'admin') ? p : 'readcopy'

/** Short status badge for an in-flight outgoing request. */
const pendingStatus = (_state: OutgoingRequest['state']): string => 'Pending'

/** One-line explanation of where an in-flight request is waiting. */
const pendingDetail = (state: OutgoingRequest['state']): string => {
  switch (state) {
    case 'pending_initiator_confirm': return 'Awaiting your confirmation'
    default: return "Awaiting the recipient's response"
  }
}

interface Recipient {
  key: string
  orgName: string
  contact: string
  permission: SharePermission
  /** Whether the recipient may re-share (recorded on the grant; enforced by Gate A). */
  allowSharing: boolean
  /** Not yet connected — Share sends a connection/share request (handshake). */
  isNew: boolean
  /** Recipient is a person (personal tenant), not an organization — shown
   *  with a person icon + "Personal" tag instead of the building. */
  isPersonal?: boolean
  /** Already has a direct grant on this folder (pre-loaded). Its permission
   *  changes and removal apply IMMEDIATELY (revoke + re-grant), not on the
   *  Share button. */
  existing?: boolean
  /** Recipient tenant id — set for `existing` rows (revoke / re-grant). */
  tenantId?: string
  organizationName?: string | null
  setAsDefaultContact?: boolean
  /** Informational only: an APPROVED reshare YOU made of inbound content. The
   *  source owner holds the actual grant, so this card is non-removable /
   *  non-editable and is NOT re-sent on Share — just a record. Pending reshares
   *  live in the separate `pendingReshares` list (editable / cancellable). */
  reshareByYou?: boolean
  reshareState?: 'pending' | 'approved'
  /** The established referral's request id — lets the resharer ASK the owner to
   *  change the recipient's access (the owner holds the grant). */
  requestId?: string
  /** Provenance (owner's view): the org that referred this recipient via an
   *  source-approved reshare. When set, the card notes "Reshared by <org>". */
  reshareVia?: string | null
  /** (#360) Why the last Share refused THIS recipient — the server's own sentence.
   *  Shown on the card (a batch can fail for several reasons at once); cleared when
   *  the card is edited or Share is pressed again (so a repeated refusal is a NEW
   *  alert, announced again). */
  error?: string
  /** (#360 step 2, on #359's refusal) The bare address reaches SEVERAL of the sender's
   *  connections (409 AMBIGUOUS_RECIPIENT): the server names them, and the card lists
   *  them to pick ONE from — a selection from N (a person may be in any number of orgs),
   *  never a yes/no. Picking targets that tenant explicitly (`recipientCompanyTenantId`)
   *  on the next Share. Listing them discloses nothing: each is the sender's own
   *  connection. Cleared with `error`. */
  candidates?: ShareCandidate[]
}

interface ShareCandidate { tenantId: string; name: string; type: string }

/** The connections a refused bare-email share named, when the refusal is #359's. */
function ambiguousCandidates(e: unknown): ShareCandidate[] | undefined {
  const err = e as { code?: unknown; candidates?: unknown } | null
  if (err?.code !== 'AMBIGUOUS_RECIPIENT' || !Array.isArray(err.candidates)) return undefined
  return err.candidates.length > 1 ? err.candidates as ShareCandidate[] : undefined
}

/** (#360) The sentence a refused share shows on its card: the server's message (its
 *  `detail` when it sent one beside a code), else a generic line. A refusal about a
 *  folder shared ALONGSIDE (the server names it, `failedFolderId`) says which one —
 *  else it reads as if it were about the folder being shared. */
function shareErrorMessage(e: unknown, folderNameOf: (id: string) => string | undefined): string {
  const err = e as { detail?: unknown; failedFolderId?: unknown } | null
  const detail = err?.detail
  const msg = typeof detail === 'string' && detail ? detail
    : e instanceof Error && e.message ? e.message : 'Could not share with this recipient'
  const failed = typeof err?.failedFolderId === 'string' ? folderNameOf(err.failedFolderId) : undefined
  return failed ? `${failed}: ${msg}` : msg
}

/** A reshare referral YOU made that's still awaiting the source owner. Editable
 *  (proposed access) + cancellable, like a normal pending share. */
interface PendingReshare {
  requestId: string
  recipientEmail: string
  permission: SharePermission
  isPersonal: boolean
}

export function ShareDialog({ folderId, folderName, isFolder = true, folderPath, myPermission, initialRecipientTenantId, alsoShare = [], onOpenWhoHasAccess, canManage = true, refreshKey, onClose }: Props) {
  // (#327 B7) Includes anything shared alongside — a chain-escape flow can pull a
  // package in as a companion, and that hands over its source just the same.
  const isPackageShare =
    sharesPackageSource(folderName, isFolder) ||
    alsoShare.some(f => sharesPackageSource(f.name, f.isFolder))
  const toast = useToast()
  const confirm = useConfirm()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [connections, setConnections] = useState<ConnectionRow[]>([])
  const [liveConnectionTenantIds, setLiveConnectionTenantIds] = useState<Set<string> | null>(null)
  const [pending, setPending] = useState<OutgoingRequest[]>([])
  // Re-sharing choice per pending request (the /outgoing payload doesn't
  // carry allow_sharing, so default to true until edited here).
  const [pendingAllow, setPendingAllow] = useState<Record<string, boolean>>({})
  const [recipients, setRecipients] = useState<Recipient[]>([])
  // Reshare referrals YOU made that still await the owner — own section, editable.
  const [pendingReshares, setPendingReshares] = useState<PendingReshare[]>([])
  // External same-tenant `.chain` targets referenced from inside this folder —
  // shared ALONGSIDE it so the chains aren't broken for the recipient.
  const [chainRefs, setChainRefs] = useState<Array<{ id: string; name: string; isFolder: boolean }>>([])
  const [loading, setLoading] = useState(true)
  const [sharing, setSharing] = useState(false)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  // (#360) Bumped after a Share that partly failed: the dialog stays open on the
  // refused cards, so its lists reload to show who now has access / is pending.
  const [reloadTick, setReloadTick] = useState(0)
  // The reverse-share seed is added on the FIRST load for that recipient only — a
  // later reload must not bring back a seed the person removed or already sent.
  const seededForRef = useRef<string | null>(null)
  // Two loads can be in flight (a reloadTick after a partial Share, a refreshKey bump
  // from Who has access): only the NEWEST may write, or a stale one that resolves last
  // brings back revoked access and drops a staged pick for that tenant.
  const loadGenRef = useRef(0)
  // Keys being sent by an in-flight Share: a reload landing mid-batch keeps them
  // staged, so their outcome still has a card to land on.
  const sendingKeysRef = useRef<Set<string>>(new Set())
  // Whether the dialog is still open when a Share finishes — closed mid-batch, its
  // toast can't point at cards that are gone.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [newOrgOpen, setNewOrgOpen] = useState(false)
  // Prefill for the "new outsider" dialog when opened from the picker's
  // "add as a new outsider" row (email → Person, other text → Organization).
  const [newOrgPrefill, setNewOrgPrefill] = useState<NewOutsiderPrefill | null>(null)
  /** When set, the new-outsider dialog is open in EDIT mode for this card. */
  const [editing, setEditing] = useState<Recipient | null>(null)
  // What was typed in the picker before drilling into the new-outsider dialog.
  // Preserved so cancelling the dialog can reopen the picker with it intact;
  // the "Add recipient" button resets it so a fresh open starts empty.
  const [pickerInitialSearch, setPickerInitialSearch] = useState('')
  // Set on a new-outsider confirm so the dialog's onClose (fired on both confirm
  // and cancel) can tell them apart: confirm → leave picker closed + reset;
  // cancel → reopen the picker with the typed text restored.
  const confirmedNewOrgRef = useRef(false)

  useEffect(() => {
    const gen = ++loadGenRef.current
    Promise.all([
      // null = the list failed to load (the level-change pre-check then refuses: unknown).
      orgConnectionsApi.list().then(({ connections }) => connections as ConnectionRow[]).catch(() => null),
      // Outsiders mid-handshake on THIS folder — a request was sent but not yet
      // accepted. Only this folder's: the outbox is the org's (an owner/admin
      // sees every folder's), and a row here can be re-levelled or withdrawn, so
      // another folder's invitation must never show up as this one's (#1075).
      // The client filter stays as well — an older API ignores `?folderId=`.
      orgConnectionsApi.listOutgoing({ folderId })
        .then(({ outgoing }) => outgoing.filter(r => isInFlight(r) && (r.sharedItemIds ?? [r.sourceFolderId]).includes(folderId)))
        .catch(() => [] as OutgoingRequest[]),
      // Orgs that already have a DIRECT grant on this folder — pre-loaded
      // as cards so the dialog shows who it's already shared with.
      orgConnectionsApi.listFolderAccess(folderId).then(({ folderAccess }) => folderAccess.filter(r => r.directGrant || r.reshareByYou)).catch(() => [] as FolderAccessRow[]),
      // External `.chain` targets inside this folder — content the recipient
      // would otherwise see as broken chains. Shared alongside the folder.
      orgConnectionsApi.getChainRefs(folderId).catch(() => [] as Array<{ id: string; name: string; isFolder: boolean }>),
    ])
      .then(([allConns, out, access, refs]) => {
        if (gen !== loadGenRef.current) return
        // Every LIVE connection's tenant, before the contact filter (the level-change
        // pre-check), set from the same load as the rest.
        // Unknown (null) unless the whole list loaded and every row is well-formed.
        const wellFormed = !!allConns && allConns.every(c => !!c?.org?.tenantId)
        setLiveConnectionTenantIds(wellFormed ? new Set(allConns!.map(c => c.org.tenantId)) : null)
        const conns = (allConns ?? []).filter(c => c?.org && c.theirDefaultContact)
        setConnections(conns)
        setPending(out)
        setChainRefs(refs)
        // Map tenant → its connection default-contact email (needed to
        // re-grant an existing recipient via the silent re-share path).
        const emailByTenant = new Map(conns.map(c => [c.org.tenantId, c.theirDefaultContact]))
        // PENDING reshares get their own section (editable / cancellable);
        // APPROVED reshares + direct grants render as Share-with cards.
        setPendingReshares(
          access
            .filter(r => r.reshareByYou && r.reshareState === 'pending' && r.requestId)
            .map(r => ({
              requestId: r.requestId as string,
              recipientEmail: r.recipientEmail ?? r.name,
              permission: toSharePerm(r.maxPermission),
              isPersonal: r.type === 'personal',
            })),
        )
        const existingCards: Recipient[] = access
          .filter(r => r.directGrant || (r.reshareByYou && r.reshareState === 'approved'))
          .map(r => ({
            key: `${r.reshareByYou ? 'reshare' : 'existing'}:${r.tenantId}`,
            orgName: r.name,
            contact: emailByTenant.get(r.tenantId) ?? r.recipientEmail ?? '',
            permission: toSharePerm(r.maxPermission),
            allowSharing: r.allowSharing,
            isNew: false,
            // A reshare-by-you card is NOT a manageable owned grant — it's
            // informational (the source owner holds the grant).
            existing: !r.reshareByYou,
            reshareByYou: r.reshareByYou,
            reshareState: r.reshareState,
            requestId: r.requestId,
            reshareVia: r.reshareVia,
            isPersonal: r.type === 'personal',
            tenantId: r.tenantId,
          }))
          // Dedup: multiple referrals to the SAME recipient (reshare → owner
          // revokes → reshare again) produce one folder-access row each, all with
          // the same card key. Show ONE card per recipient; `access` is
          // newest-first (outboundRows ORDER BY created_at DESC), so keep the latest.
          .filter((r, i, arr) => arr.findIndex(x => x.key === r.key) === i)
        // Reverse-share seed: pre-add the owner org as a fresh recipient card
        // (unless it already holds a direct grant — then the existing card
        // covers it). The drop chose this recipient; don't make the user re-pick.
        const seeded: Recipient[] = []
        if (initialRecipientTenantId && seededForRef.current !== initialRecipientTenantId) {
          const conn = conns.find(cc => cc.org.tenantId === initialRecipientTenantId)
          if (existingCards.some(r => r.tenantId === initialRecipientTenantId)) {
            seededForRef.current = initialRecipientTenantId
          } else if (conn) {
            seededForRef.current = initialRecipientTenantId
            seeded.push({
              key: conn.connectionId,
              orgName: conn.org.name,
              contact: conn.theirDefaultContact,
              permission: 'readcopy',
              allowSharing: true,
              isNew: false,
              isPersonal: conn.org.type === 'personal',
              tenantId: conn.org.tenantId,
            })
          }
          // Neither (the connections list failed): the ref stays unset, so the
          // next reload tries again.
        }
        // Preserve every STAGED recipient (new outsiders and picked connections —
        // incl. a refused card still showing its reason, #360) across a reload;
        // replace existing/reshare cards with fresh data. A staged pick whose
        // tenant now holds access is covered by its existing card.
        setRecipients(prev => {
          // A REFUSED card whose tenant now holds access (e.g. `/initiate` wrote the grant,
          // then failed) is dropped too: the access is real and shows under "Already has
          // access" — keeping it would offer to share again what already landed.
          const existingTenants = new Set(existingCards.map(r => r.tenantId))
          const staged = prev.filter(r => !r.existing && !r.reshareByYou
            && (sendingKeysRef.current.has(r.key) || !(r.tenantId && existingTenants.has(r.tenantId))))
          const stagedKeys = new Set(staged.map(r => r.key))
          return [...seeded.filter(r => !stagedKeys.has(r.key)), ...existingCards, ...staged]
        })
      })
      .finally(() => { if (gen === loadGenRef.current) setLoading(false) })
  }, [folderId, initialRecipientTenantId, refreshKey, reloadTick])

  const addedKeys = new Set(recipients.map(r => r.key))
  // Tenants already shown as existing cards — keep them out of the picker.
  const existingTenantIds = new Set(recipients.filter(r => (r.existing || r.reshareByYou) && r.tenantId).map(r => r.tenantId as string))
  // Folders shared alongside the primary: explicitly-passed `alsoShare` (e.g. the
  // chain-escape targets) plus auto-detected chain targets inside the folder.
  const sharedAlongside = Array.from(
    new Map([...alsoShare, ...chainRefs].map((f) => [f.id, f])).values(),
  ).filter((f) => f.id !== folderId)
  const remove = (key: string) => setRecipients(prev => prev.filter(r => r.key !== key))

  // New / connection card: staged edit (applied on the Share button).
  const stageEdit = (key: string, p: SharePermission, allow: boolean) =>
    setRecipients(prev => prev.map(r => r.key === key ? { ...r, permission: p, allowSharing: allow, error: undefined, candidates: undefined } : r))

  // Existing recipient: a permission/allow change applies NOW. The grant
  // model aggregates by MAX across grants, so a plain re-share can't LOWER
  // access — the direct grant(s) are REPLACED by one fresh grant at the new
  // level, in ONE server step (`replacesAccess`, I4): refused, nothing changes
  // and the old access stands (it used to be a revoke, then a separate re-share,
  // and a refusal in between left the recipient with nothing). Effective access
  // can't drop below an inherited ancestor grant, which is expected. (Not PATCH
  // /folder-access: it resolves nearest-wins where these cards show MAX,
  // re-points a `hidden` grant to visible, and skips /initiate's source gates — dw I7.)
  const applyExistingChange = async (r: Recipient, p: SharePermission, allow: boolean) => {
    if (p === r.permission && allow === r.allowSharing) return
    if (!r.contact) { toast.error(`No contact on file for ${r.orgName} — manage in Who has access`); return }
    // Refuse up front what the server is sure to refuse: an outsider is re-granted View or
    // Edit only, and only over a live connection.
    if (p !== 'readcopy' && p !== 'readwrite') {
      toast.error(`${r.orgName}'s access can't be changed here — manage it in Who has access`)
      return
    }
    // Unknown (the connections list didn't load) refuses too.
    if (!liveConnectionTenantIds) {
      toast.error(`Couldn't check your connection with ${r.orgName} — try again, or change it in Who has access`)
      setReloadTick(t => t + 1)
      return
    }
    if (!liveConnectionTenantIds.has(r.tenantId as string)) {
      toast.error(`${r.orgName} is no longer one of your connections — manage their access in Who has access`)
      return
    }
    setBusyKey(r.key)
    try {
      await orgConnectionsApi.initiate({
        recipientEmail: r.contact,
        recipientCompanyTenantId: r.tenantId,
        sourceFolderId: folderId,
        permission: p,
        allowSharing: allow,
        note: `Updated access to ${folderName}`,
        replacesAccess: true,
      })
      setRecipients(prev => prev.map(x => x.key === r.key ? { ...x, permission: p, allowSharing: allow } : x))
      toast.success(`Updated ${r.orgName}'s access`)
    } catch (e) {
      // Refused whole: the old access stands (nothing to reload).
      toast.error(e instanceof Error ? e.message : 'Failed to update access')
    } finally {
      setBusyKey(null)
    }
  }

  // Existing recipient removal = revoke the direct grant (confirmed).
  const removeExisting = async (r: Recipient) => {
    const ok = await confirm({
      title: `Remove ${r.orgName}'s access?`,
      message: (
        <span>
          Revoke this folder's direct grant to <strong>{r.orgName}</strong>?
          Inherited grants on ancestor folders (if any) are not touched.
        </span>
      ),
      type: 'danger',
      confirmText: 'Remove access',
    })
    if (!ok) return
    setBusyKey(r.key)
    try {
      const res = await orgConnectionsApi.revokeFolderAccess(folderId, r.tenantId as string)
      if (res.revoked > 0) toast.success(`Removed ${r.orgName}'s access`)
      else toast.warning('No direct grant found to remove (grant is inherited)')
      remove(r.key)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to remove')
    } finally {
      setBusyKey(null)
    }
  }

  // A pending request's re-sharing choice: this session's edit, else what the
  // server stored (null = allowed). Never a default of "on" over a stored "off" —
  // the next level change would send it and turn re-sharing back on.
  const allowOf = (p: OutgoingRequest): boolean => pendingAllow[p.requestId] ?? (p.allowSharing !== false)

  // Pending request: edit the requested access (before it's accepted).
  const applyPendingChange = async (p: OutgoingRequest, perm: SharePermission, allow: boolean) => {
    if (perm === toSharePerm(p.grantedPermission) && allow === allowOf(p)) return
    setBusyKey(p.requestId)
    try {
      const res = await orgConnectionsApi.updateRequestAccess(p.requestId, perm, allow)
      // The server clamps to the caller's access on the folder — show what it stored.
      const stored = res.permission ?? perm
      setPending(prev => prev.map(x => x.requestId === p.requestId ? { ...x, grantedPermission: stored } : x))
      setPendingAllow(prev => ({ ...prev, [p.requestId]: allow }))
      toast.success('Updated requested access')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update')
    } finally {
      setBusyKey(null)
    }
  }

  // A request can share more than this folder (folders bundled with it) —
  // withdrawing it withdraws them all, so say so.
  const withAlso = (p: OutgoingRequest): string => {
    const others = (p.sharedItemIds ?? []).filter(id => id !== folderId).length
    return others > 0 ? ` and ${others} other ${others === 1 ? 'item' : 'items'}` : ''
  }

  // Pending request: withdraw it (initiator cancel).
  const cancelPending = async (p: OutgoingRequest) => {
    const ok = await confirm({
      title: 'Cancel this request?',
      message: <span>Withdraw the pending request to share <strong>{folderName}</strong>{withAlso(p)} with <strong>{p.recipientEmail}</strong>?</span>,
      type: 'danger',
      confirmText: 'Cancel request',
    })
    if (!ok) return
    setBusyKey(p.requestId)
    try {
      await orgConnectionsApi.reject(p.requestId)
      setPending(prev => prev.filter(x => x.requestId !== p.requestId))
      toast.success('Request cancelled')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to cancel')
    } finally {
      setBusyKey(null)
    }
  }

  // Pending reshare: edit the proposed access (re-clamped server-side to your
  // own ceiling on the owner's content).
  const editReshareAccess = async (p: PendingReshare, perm: SharePermission) => {
    if (perm === p.permission) return
    setBusyKey(p.requestId)
    try {
      const res = await orgConnectionsApi.updateReshareReferralAccess(p.requestId, perm === 'readwrite' ? 'readwrite' : 'readcopy')
      setPendingReshares(prev => prev.map(x => x.requestId === p.requestId ? { ...x, permission: toSharePerm(res.permission) } : x))
      toast.success('Updated requested access')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update')
    } finally {
      setBusyKey(null)
    }
  }

  // Pending reshare: withdraw it (before the owner decides).
  const cancelReshare = async (p: PendingReshare) => {
    const ok = await confirm({
      title: 'Cancel this reshare?',
      message: <span>Withdraw the pending reshare to <strong>{p.recipientEmail}</strong>? The content owner won't be asked.</span>,
      type: 'danger',
      confirmText: 'Cancel reshare',
    })
    if (!ok) return
    setBusyKey(p.requestId)
    try {
      await orgConnectionsApi.cancelReshareReferral(p.requestId)
      setPendingReshares(prev => prev.filter(x => x.requestId !== p.requestId))
      toast.success('Reshare cancelled')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to cancel')
    } finally {
      setBusyKey(null)
    }
  }

  // Approved reshare: the content OWNER holds the grant, so the resharer can't
  // change the recipient's access here — not even by notifying the owner. The
  // picker is read-only; a click just explains who can change it.
  const reshareReadOnlyNotice = (r: Recipient) =>
    toast.info(`Only the content owner can change ${r.orgName}'s access — please ask them to update it.`)

  const addConnection = (c: ConnectionRow) => {
    if (addedKeys.has(c.connectionId)) return
    setRecipients(prev => [...prev, {
      key: c.connectionId, orgName: c.org.name, contact: c.theirDefaultContact,
      permission: 'readcopy', allowSharing: true, isNew: false,
      isPersonal: c.org.type === 'personal',
      tenantId: c.org.tenantId,
    }])
  }

  const addNewOrg = (v: { email: string; organizationName: string; isOrganization: boolean; setAsDefaultContact: boolean }) => {
    const key = `new:${v.email.toLowerCase()}`
    if (addedKeys.has(key)) return
    setRecipients(prev => [...prev, {
      key, orgName: v.isOrganization && v.organizationName ? v.organizationName : v.email, contact: v.email,
      permission: 'readcopy', allowSharing: true, isNew: true,
      isPersonal: !v.isOrganization,
      organizationName: v.isOrganization ? v.organizationName : null,
      setAsDefaultContact: v.setAsDefaultContact,
    }])
  }

  // Edit an existing new-outsider card in place (preserve its permission +
  // allow-sharing; update org/email). Key changes if the email changed.
  const editNewOrg = (oldKey: string, v: { email: string; organizationName: string; isOrganization: boolean; setAsDefaultContact: boolean }) => {
    const newKey = `new:${v.email.toLowerCase()}`
    // Another card already holds that address — two cards on one key would share
    // one Share outcome (#360 applies it by key).
    if (newKey !== oldKey && addedKeys.has(newKey)) { toast.info(`${v.email} is already a recipient`); return }
    setRecipients(prev => prev.map(r => r.key !== oldKey ? r : {
      ...r,
      key: newKey,
      orgName: v.isOrganization && v.organizationName ? v.organizationName : v.email,
      contact: v.email,
      isPersonal: !v.isOrganization,
      organizationName: v.isOrganization ? v.organizationName : null,
      setAsDefaultContact: v.setAsDefaultContact,
      error: undefined,
      candidates: undefined,
    }))
  }

  // Edit a CONNECTION / seeded card's contact email in place. Unlike
  // editNewOrg this PRESERVES the connection identity (key + tenantId + org
  // name) — the share still targets that connected tenant; only the
  // notification email changes (and, if "set as default contact" is ticked,
  // the persisted recipient-side default contact on the connection — now that
  // setDefaultContact writes the right side, that's a real correction path).
  const editConnectionContact = (key: string, v: { email: string; setAsDefaultContact: boolean }) =>
    setRecipients(prev => prev.map(r => r.key === key ? { ...r, contact: v.email.trim(), setAsDefaultContact: v.setAsDefaultContact, error: undefined, candidates: undefined } : r))

  // (#360 step 2) The sender picks which of the named connections a refused bare-email
  // card means. The card becomes that connection (its tenant targeted explicitly on the
  // next Share) and keeps the address typed, which still gets the notification. A card
  // for that connection already staged wins — two cards on one tenant would send twice.
  const pickCandidate = (key: string, c: ShareCandidate) => {
    if (recipients.some(r => r.key !== key && r.tenantId === c.tenantId)) {
      toast.info(`${c.name} is already a recipient`)
      setRecipients(prev => prev.filter(r => r.key !== key))
      return
    }
    // Keyed as that connection's card would be, so the picker no longer offers it.
    const conn = connections.find(cc => cc.org.tenantId === c.tenantId)
    setRecipients(prev => prev.map(r => r.key !== key ? r : {
      ...r,
      key: conn?.connectionId ?? `tenant:${c.tenantId}`,
      tenantId: c.tenantId,
      orgName: c.name,
      isPersonal: c.type === 'personal',
      isNew: false,
      organizationName: null,
      error: undefined,
      candidates: undefined,
    }))
  }

  // Only NEW / connection recipients are sent here — existing recipients'
  // changes already applied immediately (revoke + re-grant).
  const toSend = recipients.filter(r => !r.existing && !r.reshareByYou)

  const handleShare = async () => {
    if (toSend.length === 0) return
    setSharing(true)
    // #1316 review round 2 (owner, 2026-10-28): the Share the person pressed FINISHES as the identity that pressed it —
    // every recipient's request goes as it, never as the next session; nothing is shown once that session ended
    const batch = pinnedBatch()
    const sending = new Set(toSend.map(r => r.key))
    sendingKeysRef.current = sending
    setRecipients(prev => prev.map(r => sending.has(r.key) && r.error ? { ...r, error: undefined, candidates: undefined } : r))
    const folderNameOf = (id: string) => sharedAlongside.find(f => f.id === id)?.name ?? (id === folderId ? folderName : undefined)
    let ok = 0
    // (#360) Each refused recipient keeps the server's reason, shown on its card —
    // never collapsed into a count (one batch can fail for different reasons).
    const failed = new Map<string, string>()
    const named = new Map<string, ShareCandidate[]>()  // #359's refusal: the connections it named
    const sent = new Set<string>()
    let referrals = 0  // reshares routed to owner-approval (not shared yet)
    let notConnected = 0  // reshare blocked on a missing connection → sent a connect request instead
    const notConnectedContacts: string[] = []  // their addresses, for an accurate toast
    // ONE request per recipient carries the primary folder PLUS each EXTERNAL
    // chain target (e.g. `oo`, referenced by an `oo.chain` inside the folder), so
    // the recipient accepts ONCE and every folder resolves for them — instead of
    // N separate requests that each needed their own accept and landed one folder.
    for (const r of toSend) {
      try {
        const resp = await batch.run(() => orgConnectionsApi.initiate({
          recipientEmail: r.contact,
          sourceFolderId: folderId,
          additionalFolderIds: sharedAlongside.map(ref => ref.id),
          permission: r.permission,
          note: `Shared ${folderName} via Share dialog`,
          allowSharing: r.allowSharing,
          recipientIsPersonal: r.isPersonal,
          // Known connection (picked / seeded / contact-edited): target the
          // TENANT, not just the email, so the self-connection guard is
          // tenant-based — a stored contact that happens to equal our own
          // address can't misfire (protocol Gate 1a).
          ...(r.tenantId ? { recipientCompanyTenantId: r.tenantId } : {}),
          ...(r.isNew ? { organizationName: r.organizationName ?? null } : {}),
          ...(r.setAsDefaultContact ? { setAsDefaultContact: true } : {}),
        }))
        // A reshare to an unconnected recipient comes back as a deferred connect
        // — the server makes a connect request and fires the referral once it's
        // accepted. Surface that, not a misleading "Shared".
        if (resp.deferredReshareConnect) { notConnected++; notConnectedContacts.push(r.contact) }
        else { ok++; if (resp.reshareReferral) referrals++ }
        sent.add(r.key)
      } catch (e) {
        failed.set(r.key, shareErrorMessage(e, folderNameOf))
        const c = ambiguousCandidates(e)
        if (c) named.set(r.key, c)
      }
    }
    setSharing(false)
    sendingKeysRef.current = new Set()
    if (batch.ended()) return
    const fail = failed.size
    const open = mountedRef.current
    // Surfaced alongside any success — a mixed batch (some shared, some deferred)
    // must not silently swallow the connection request(s) that went out.
    const connNote = notConnected > 0 ? ` · ${notConnected} reshare${notConnected === 1 ? '' : 's'} pending confirmation` : ''
    const failNote = !fail ? '' : open
      ? ` · couldn't share with ${fail} — see ${fail === 1 ? 'its card' : 'their cards'}`
      : fail === 1 ? ` · couldn't share with 1: ${[...failed.values()][0]}` : ` · couldn't share with ${fail}`
    if (fail > 0) {
      // (#360) Stay open on the refused cards, each showing why. What went out is
      // dropped from the staged list (never re-sent) and the lists reload so it
      // shows under Pending / Already has access.
      setRecipients(prev => prev
        .filter(r => !sent.has(r.key))
        .map(r => failed.has(r.key) ? { ...r, error: failed.get(r.key), candidates: named.get(r.key) } : r))
      // Always reload (not only when something went out): it also re-runs the
      // staged filter now the batch is over, so a refused card whose tenant gained
      // access meanwhile (kept while it was being sent) doesn't linger.
      setReloadTick(t => t + 1)
    }
    if (ok > 0) {
      // Reshare of inbound content is an source-approved referral, not a
      // completed share — say so plainly instead of a misleading "Shared".
      if (referrals === ok) {
        toast.success(`Reshare of "${folderName}" sent — waiting for the content owner to approve${connNote}${failNote}`)
      } else {
        const extra = sharedAlongside.length ? ` (+${sharedAlongside.length} more ${sharedAlongside.length === 1 ? 'folder' : 'folders'})` : ''
        const pending = referrals > 0 ? ` · ${referrals} awaiting owner approval` : ''
        toast.success(`Shared "${folderName}"${extra} with ${ok} ${ok === 1 ? 'recipient' : 'recipients'}${pending}${connNote}${failNote}`)
      }
      if (fail === 0) onClose()
    } else if (notConnected > 0) {
      toast.info((notConnected === 1
        ? `Reshare pending confirmation from ${notConnectedContacts[0] ?? 'them'} — connection request sent.`
        : `Reshare pending confirmation from ${notConnected} recipients — connection requests sent.`) + failNote)
      if (fail === 0) onClose()
    } else {
      toast.error(!open
        ? (fail === 1 ? `Couldn't share: ${[...failed.values()][0]}` : `Couldn't share with ${fail} recipients`)
        : fail === 1 ? "Couldn't share — see the reason on the recipient's card" : `Couldn't share with ${fail} recipients — see the reason on each card`)
    }
  }

  // One recipient card — reused by the "New request" (staged outsiders) and the
  // "Share with" (established access) sections, which split `recipients` by `isNew`.
  const renderRecipientCard = (r: Recipient) => (
    <div key={r.key} data-testid="share-recipient-card" className={`flex items-center gap-3 px-3 py-2 themed-bg-tertiary border rounded-md ${r.error ? 'border-red-500/60' : 'themed-border'}`}>
      {/* Identity chip in the same shape Who-has-access uses for principals:
          a filled round avatar, initials for a personal drive and the org
          glyph for an organization — not a bare outline icon. */}
      {r.isPersonal ? (
        <span
          title={r.orgName}
          className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0 select-none ${avatarBg(r.contact || r.orgName)}`}
        >
          {getInitials(r.orgName)}
        </span>
      ) : (
        <span className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
          <Building2 size={14} className="text-white" />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium themed-text truncate flex items-center gap-1.5">
          {r.orgName}
          {r.isPersonal && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 shrink-0 font-normal">Personal</span>}
          {/* `existing` needs no badge now — the "Already has access" heading says
              it. `reshared` stays: it means the content owner holds the grant,
              which the heading alone doesn't convey. */}
          {r.isNew && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0">new request</span>}
          {r.reshareByYou && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 shrink-0">reshared</span>}
          {/* Edit the recipient's email — full edit for a new outsider (name +
              email), email-only for a known connection. Hidden for `existing`
              and reshare records (the owner holds the grant). */}
          {!r.existing && !r.reshareByYou && (
            <button onClick={() => setEditing(r)} disabled={sharing} className="p-0.5 themed-text-muted hover:themed-text transition-colors shrink-0 disabled:opacity-50" aria-label={r.isNew ? 'Edit outsider' : 'Edit contact email'}>
              <SquarePen size={13} />
            </button>
          )}
        </div>
        <div className="text-xs themed-text-muted truncate">{r.contact || '—'}</div>
        {/* Owner's view: this recipient came in via another org's source-approved
            reshare — name who referred them. */}
        {r.reshareVia && (
          <div className="text-[10px] themed-text-muted truncate opacity-80">Reshared by {r.reshareVia}</div>
        )}
        {/* (#360) Why the last Share refused this recipient — the server's sentence. */}
        {r.error && (
          <div role="alert" data-testid="share-recipient-error" className="mt-0.5 text-xs text-red-400 break-words">{r.error}</div>
        )}
        {/* (#360 step 2) #359's refusal named several of your connections: pick one. */}
        {r.candidates && r.candidates.length > 0 && (
          <div data-testid="share-recipient-candidates" className="mt-1">
            <div className="text-[11px] themed-text-muted mb-1">Which one did you mean? Pick it, then press Share.</div>
            <ul role="group" aria-label={`Connections for ${r.contact}`} className="flex flex-col gap-1 max-h-40 overflow-y-auto">
              {r.candidates.map(c => (
                <li key={c.tenantId}>
                  <button
                    type="button"
                    data-testid="share-recipient-candidate"
                    onClick={() => pickCandidate(r.key, c)}
                    disabled={sharing}
                    className="w-full text-left text-xs px-2 py-1 rounded border themed-border themed-text hover:themed-bg-secondary disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {c.type === 'personal' ? <User size={12} className="shrink-0" /> : <Building2 size={12} className="shrink-0" />}
                    <span className="truncate">{c.name}</span>
                    {c.type === 'personal' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 shrink-0">Personal</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {/* While Share runs, a card being sent can't change: the batch's outcome is
          applied to cards by key afterwards (#360), so an edit mid-flight would
          re-send what went out, or stamp a stale refusal on a corrected card. */}
      {busyKey === r.key || (sharing && !r.existing && !r.reshareByYou) ? (
        <Loader2 size={14} className="animate-spin themed-text-muted shrink-0" />
      ) : (
        <RecipientAccessPicker
          permission={r.permission}
          allowSharing={r.allowSharing}
          maxPermission={myPermission}
          // Read-only for reshare-by-you (the content owner holds the grant) AND
          // for an existing recipient a non-manager (member) is viewing.
          readOnly={r.reshareByYou || (r.existing && !canManage)}
          onReadOnlyClick={() =>
            r.reshareByYou
              ? reshareReadOnlyNotice(r)
              : toast.info(`You need Manage & Edit access on this folder to change ${r.orgName}'s access.`)}
          // Direct grants apply immediately; staged recipients edit locally.
          onChange={(p, allow) =>
            r.existing ? void applyExistingChange(r, p, allow)
              : stageEdit(r.key, p, allow)}
          // Remove folds into the picker popover; absent for non-removable cards.
          onRemove={(r.reshareByYou || (r.existing && !canManage)) ? undefined : () => (r.existing ? void removeExisting(r) : remove(r.key))}
        />
      )}
    </div>
  )

  // New outsiders you're inviting ("new request") get their own section above
  // Pending; established access + picked connections stay in "Share with".
  const newRequests = recipients.filter(r => r.isNew)
  const sharedWith = recipients.filter(r => !r.isNew)
  // "Share with" used to lump together drives you just PICKED and drives that
  // already have access, separated only by a small badge — so it wasn't clear
  // who the Share button was about to affect. Split them (same shape as
  // ShareInternallyDialog): staged picks first, established access below.
  const stagedPicks = sharedWith.filter(r => !r.existing && !r.reshareByYou)
  const establishedAccess = sharedWith.filter(r => r.existing || r.reshareByYou)

  return createPortal(
    // z-[100]: above the mobile header (z-[60]); desktop unaffected.
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} onKeyDown={e => { if (e.key === 'Escape') onClose() }} />
      <div data-share-modal className={`relative z-10 rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col ${themeClass} themed-bg-secondary`}>
        {/* Header — mirrors the Who-has-access dialog. Pinned (shrink-0). */}
        <div className="flex items-center justify-between px-6 pt-5 shrink-0">
          <h3 className="text-lg font-semibold themed-text flex items-center gap-1.5 flex-wrap min-w-0">
            <span>Share</span>
            <Tooltip content={folderPath ?? ''} always followCursor>
              <span className="inline-flex items-center gap-1.5 themed-text-muted font-normal min-w-0">
                {isFolder
                  ? <Folder size={18} className="text-yellow-500 shrink-0" />
                  : <File size={18} className="themed-text-muted shrink-0" />}
                <span className="truncate">{folderName}{sharedAlongside.length > 0 ? ',' : ''}</span>
              </span>
            </Tooltip>
            {/* Header is a summary — show the first few folders shared alongside,
                then "+N more" (full list lives in the chain-folders section below). */}
            {sharedAlongside.slice(0, 3).map((ref, i, shown) => (
              <span key={ref.id} className="inline-flex items-center gap-1.5 themed-text-muted font-normal min-w-0">
                {ref.isFolder
                  ? <Folder size={18} className="text-yellow-500 shrink-0" />
                  : <File size={18} className="themed-text-muted shrink-0" />}
                <span className="truncate max-w-[10rem]">{ref.name}{i < shown.length - 1 ? ',' : ''}</span>
              </span>
            ))}
            {sharedAlongside.length > 3 && (
              <Tooltip content={sharedAlongside.slice(3).map(r => r.name).join(', ')} always followCursor>
                <span className="themed-text-muted font-normal whitespace-nowrap cursor-default">+{sharedAlongside.length - 3} more</span>
              </Tooltip>
            )}
          </h3>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text transition-colors rounded shrink-0 ml-3">
            <X size={18} />
          </button>
        </div>

        {/* Add recipient — pinned, and OUTSIDE the scroll region below so its
            picker popover (absolute) isn't clipped by overflow. */}
        <div className="px-6 pt-4 shrink-0">
          <div className="relative inline-block">
            <button
              onClick={() => setPickerOpen(o => { if (!o) setPickerInitialSearch(''); return !o })}
              disabled={loading || sharing}
              className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add recipient
            </button>
            {pickerOpen && (
              <ConnectionAddPicker
                connections={connections.filter(c => !existingTenantIds.has(c.org.tenantId))}
                addedKeys={addedKeys}
                initialSearch={pickerInitialSearch}
                onPick={addConnection}
                onAddNew={(prefill) => {
                  // Remember what was typed so a later cancel can restore it.
                  setPickerInitialSearch(prefill.email ?? prefill.organizationName ?? '')
                  setPickerOpen(false); setNewOrgPrefill(prefill); setNewOrgOpen(true)
                }}
                onClose={() => setPickerOpen(false)}
              />
            )}
          </div>
        </div>

        {/* Recipient sections — the ONE scroll region. Caps the modal to
            max-h-[85vh]; header + add-row above and footer below stay pinned,
            so they're always reachable no matter how many cards. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-2 pb-2 thin-scrollbar">
          {/* New request — brand-new outsiders you're inviting (the handshake is
              sent on Share). Leads, above Pending, so the not-yet-sent cards are first. */}
          {newRequests.length > 0 && (
            <>
              <p className="text-xs themed-text-muted mt-4 mb-2">New request{newRequests.length > 1 ? 's' : ''}</p>
              <div className="space-y-1.5">
                {newRequests.map(renderRecipientCard)}
              </div>
            </>
          )}

          {/* Pending — your in-flight requests + reshares awaiting the content
              owner. Editable / cancellable, like a normal pending share. */}
          {(pending.length > 0 || pendingReshares.length > 0) && (
            <>
              <p className="text-xs themed-text-muted mt-4 mb-2">Pending</p>
              <div className="space-y-1.5">
                {pendingReshares.map(p => (
                  <div key={p.requestId} className="flex items-center gap-3 px-3 py-2 themed-bg-tertiary border themed-border rounded-md">
                    {p.isPersonal
                      ? <User size={14} className="themed-text-muted shrink-0" />
                      : <Building2 size={14} className="themed-text-muted shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium themed-text truncate flex items-center gap-1.5">
                        <span className="truncate">{p.recipientEmail}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0 whitespace-nowrap">awaiting owner</span>
                      </div>
                      <div className="text-xs themed-text-muted truncate">Reshare · waiting for the content owner to approve</div>
                    </div>
                    {busyKey === p.requestId ? (
                      <Loader2 size={14} className="animate-spin themed-text-muted shrink-0" />
                    ) : (
                      <RecipientAccessPicker
                        permission={p.permission}
                        allowSharing
                        maxPermission={myPermission}
                        onChange={(perm) => void editReshareAccess(p, perm)}
                        onRemove={() => void cancelReshare(p)}
                        removeLabel="Cancel reshare"
                      />
                    )}
                  </div>
                ))}
                {pending.map(p => (
                  // testids, not text: "Pending" is a generic word and an
                  // unscoped getByText('Pending') matched a hidden menu button
                  // elsewhere on the page instead of this badge (e2e
                  // org-connections-share, 2026-08-07).
                  <div key={p.requestId} data-testid="pending-recipient" className="flex items-center gap-3 px-3 py-2 themed-bg-tertiary border themed-border rounded-md">
                    <Building2 size={14} className="themed-text-muted shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium themed-text truncate flex items-center gap-1.5">
                        <span className="truncate">{p.recipientEmail}</span>
                        <span data-testid="pending-status" className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0">{pendingStatus(p.state)}</span>
                      </div>
                      <div className="text-xs themed-text-muted truncate">{pendingDetail(p.state)}</div>
                    </div>
                    {busyKey === p.requestId ? (
                      <Loader2 size={14} className="animate-spin themed-text-muted shrink-0" />
                    ) : (
                      <RecipientAccessPicker
                        permission={toSharePerm(p.grantedPermission)}
                        allowSharing={allowOf(p)}
                        maxPermission={myPermission}
                        onChange={(perm, allow) => void applyPendingChange(p, perm, allow)}
                        onRemove={() => void cancelPending(p)}
                        removeLabel="Cancel request"
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Sharing with — connections you PICKED this session (nothing sent
              yet). Staged new outsiders live in "New request" above. */}
          {stagedPicks.length > 0 && (
            <>
              <p className="text-xs themed-text-muted mt-4 mb-2">Sharing with <span className="opacity-70">{stagedPicks.length}</span></p>
              <div className="space-y-1.5">
                {stagedPicks.map(renderRecipientCard)}
              </div>
            </>
          )}

          {/* Already has access — established shares + approved reshare records. */}
          {establishedAccess.length > 0 && (
            <>
              <p className="text-xs themed-text-muted mt-4 mb-2">Already has access <span className="opacity-70">{establishedAccess.length}</span></p>
              <div className="space-y-1.5">
                {establishedAccess.map(renderRecipientCard)}
              </div>
            </>
          )}

          {sharedWith.length === 0 && (
            <>
              <p className="text-xs themed-text-muted mt-4 mb-2">Share with</p>
              <p className="py-5 text-center themed-text-muted text-sm border border-dashed themed-border rounded-md">
                {newRequests.length > 0 ? 'No one has access yet.' : 'No recipients yet — use “Add recipient” above.'}
              </p>
            </>
          )}

          {/* (#327 B7) Sharing a PACKAGE folder hands over its source. This is
              long-standing folder-share semantics, not new behavior — but "let
              them use my app" and "give them my source" are the same click, so
              an admin can extend far more trust than they meant to. The narrow
              act exists (a share LINK runs the app and exposes nothing), so the
              honest fix is to name the difference at the moment of choosing.
              Disclosure only — it changes nothing about the share itself. */}
          {isPackageShare && (
            <div className="mt-3 px-3 py-2 rounded-md border border-dashed themed-border text-xs">
              <div className="flex items-start gap-1.5 themed-text-muted">
                <Shield size={14} className="shrink-0 mt-0.5" />
                <span>
                  Recipients get <span className="themed-text">{folderName}</span>'s full
                  contents — including its source code and configuration
                  {' '}(<span className="themed-text">_scripts/</span>, <span className="themed-text">.config/</span>),
                  and their AI assistant can read it too. To let someone
                  {' '}<span className="themed-text">run</span> the app without seeing how it
                  works, share a link instead.
                </span>
              </div>
            </div>
          )}

          {/* External chain targets inside this folder — auto-included in the
              share so the chains aren't broken for the recipient (e.g. an
              `oo.chain` inside `pp` pulls in `oo`). Informational. */}
          {chainRefs.length > 0 && (
            <div className="mt-3 px-3 py-2 rounded-md border border-dashed themed-border text-xs">
              <div className="flex items-center gap-1.5 themed-text-muted mb-1.5">
                <span><span className="themed-text">{folderName}</span> includes these chain folders, so they're shared too:</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {chainRefs.map(ref => (
                  <span key={ref.id} className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded themed-bg-tertiary themed-text whitespace-nowrap">
                    {/* Same .chain look as the drive view: base folder/file icon
                        with the grey Link2 corner chip (FileIcon overlay="chain"). */}
                    <FileIcon name={ref.name} isFolder={ref.isFolder} overlay="chain" size={16} />
                    <span className="truncate max-w-[12rem]">{ref.name}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer — pinned below the scroll region (shrink-0). */}
        <div className="px-6 py-4 border-t themed-border shrink-0 flex items-center justify-between gap-2">
            {onOpenWhoHasAccess && canManage ? (
              <button
                onClick={onOpenWhoHasAccess}
                className="flex items-center gap-1 text-xs themed-text-muted hover:themed-text transition-colors"
              >
                <Shield size={13} /> Who has access
              </button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text transition-colors">Cancel</button>
              <button
                onClick={() => void handleShare()}
                disabled={toSend.length === 0 || sharing}
                className="px-4 py-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50"
              >
                {sharing ? 'Sharing…' : `Share${toSend.length ? ` (${toSend.length})` : ''}`}
              </button>
            </div>
          </div>
      </div>

      {(newOrgOpen || editing) && (
        <ShareToNewOrgDialog
          folderName={folderName}
          isFolder={isFolder}
          folderPath={folderPath}
          editing={!!editing}
          // A known connection (seeded / picker-added) edits email-only; a
          // new outsider edits the full form.
          lockOrganization={!!editing && !editing.isNew}
          // New outsider: seed from what was typed in the picker (email → Person
          // with the email prefilled; other text → Organization with the name
          // prefilled and the email left for the user to enter, still required).
          initialEmail={editing?.contact ?? newOrgPrefill?.email}
          initialOrganizationName={editing?.organizationName ?? editing?.orgName ?? newOrgPrefill?.organizationName ?? ''}
          initialIsOrganization={editing ? (editing.isNew ? editing.organizationName != null : !editing.isPersonal) : (newOrgPrefill?.isOrganization ?? true)}
          initialSetAsDefaultContact={editing?.setAsDefaultContact ?? false}
          onConfirm={(v) => {
            if (!editing) { confirmedNewOrgRef.current = true; addNewOrg(v); return }
            if (editing.isNew) { editNewOrg(editing.key, v); return }
            editConnectionContact(editing.key, v)
          }}
          onClose={() => {
            // onClose fires on confirm AND cancel. A cancelled NEW outsider (no
            // `editing`) reopens the picker with the typed text restored; a
            // confirmed one — or any edit of an existing card — just closes.
            const cancelledNewOutsider = !editing && !confirmedNewOrgRef.current
            confirmedNewOrgRef.current = false
            setNewOrgOpen(false); setEditing(null); setNewOrgPrefill(null)
            if (cancelledNewOutsider) setPickerOpen(true)
          }}
        />
      )}
    </div>,
    document.body,
  )
}

/** Prefill for the "new outsider" dialog, derived from what the user typed in
 *  the picker filter: a valid email → a Person (email prefilled); any other text
 *  → an Organization (name prefilled, the email entered manually next). */
export type NewOutsiderPrefill = { email?: string; organizationName?: string; isOrganization: boolean }
const OUTSIDER_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** Add picker: the filter doubles as "enter an email". Matching connections list
 *  above; a value that isn't a connection surfaces an "add as a new outsider" row
 *  that prefills the outsider dialog (Person for an email, Organization otherwise). */
function ConnectionAddPicker({ connections, addedKeys, initialSearch, onPick, onAddNew, onClose }: {
  connections: ConnectionRow[]
  addedKeys: Set<string>
  initialSearch?: string
  onPick: (c: ConnectionRow) => void
  onAddNew: (prefill: NewOutsiderPrefill) => void
  onClose: () => void
}) {
  // Seeded from initialSearch on mount: an empty string for a fresh open, or the
  // previously-typed text when reopened after cancelling the new-outsider dialog.
  const [search, setSearch] = useState(initialSearch ?? '')
  const popRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (popRef.current && !popRef.current.contains(e.target as Node)) onClose() }
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler) }
  }, [onClose])
  const raw = search.trim()
  const q = raw.toLowerCase()
  const avail = connections.filter(c =>
    !addedKeys.has(c.connectionId) &&
    (!q || c.org.name.toLowerCase().includes(q) || c.theirDefaultContact.toLowerCase().includes(q)),
  )
  // The typed value isn't an existing connection → offer to add it as a new
  // outsider. A valid email becomes a Person; anything else, an Organization.
  const exactMatch = connections.some(c =>
    c.theirDefaultContact.toLowerCase() === q || c.org.name.toLowerCase() === q)
  const isEmail = OUTSIDER_EMAIL_RE.test(raw)
  const showAddOutsider = raw.length > 0 && !exactMatch
  const prefill: NewOutsiderPrefill = isEmail
    ? { email: raw, isOrganization: false }
    : { organizationName: raw, isOrganization: true }
  return (
    <div ref={popRef} className="absolute top-full mt-1 left-0 z-20 w-72 rounded-md shadow-xl border themed-border themed-bg-secondary" onClick={e => e.stopPropagation()}>
      <div className="p-2 border-b themed-border-secondary">
        <input
          autoFocus
          type="text"
          placeholder="Filter connections or enter an email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-2 py-1 text-xs themed-bg-primary themed-text rounded border themed-border-secondary focus:outline-none focus:ring-1 focus:ring-purple-500"
        />
      </div>
      {/* Cap the connection list at ~5 rows (rows are ~28px; 5×28 + the py-1
          top padding ≈ 144px). More connections scroll within the list, with a
          slim, subtle scrollbar (thin-scrollbar) instead of the global 8px one. */}
      <div className="max-h-36 overflow-y-auto py-1 thin-scrollbar">
        {avail.length === 0 ? (
          // With a query, the "add as a new outsider" row below covers the empty
          // case; only show the idle empty-state when nothing is typed.
          raw ? null : <p className="px-3 py-2 text-xs themed-text-muted">No connections to add</p>
        ) : avail.map(c => (
          <button
            key={c.connectionId}
            onClick={() => { onPick(c); onClose() }}
            className="w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 themed-text hover:themed-bg-tertiary cursor-pointer"
          >
            {c.org.type === 'personal'
              ? <User size={12} className="themed-text-muted shrink-0" />
              : <Building2 size={12} className="themed-text-muted shrink-0" />}
            <span className="truncate">{c.org.name}</span>
            {c.org.type === 'personal' && <span className="text-[9px] px-1 py-0.5 rounded bg-sky-500/15 text-sky-300 shrink-0">Personal</span>}
            <span className="ml-auto text-[10px] themed-text-muted truncate max-w-[45%]">{c.theirDefaultContact}</span>
          </button>
        ))}
      </div>
      {showAddOutsider && (
        <div className="border-t themed-border-secondary">
          <button
            onClick={() => { onAddNew(prefill); onClose() }}
            className="w-full px-3 py-2 text-left text-xs flex items-center gap-2.5 hover:themed-bg-tertiary cursor-pointer"
          >
            {/* Icon spans both lines (larger + vertically centered). */}
            {isEmail
              ? <UserPlus size={18} className="text-blue-400 shrink-0" />
              : <Building2 size={18} className="text-blue-400 shrink-0" />}
            <span className="min-w-0">
              {/* Same shape for both: an email shares to the address; other text
                  shares to an organization (quoted). Subline flags it as new. */}
              <span className="block themed-text">
                Share to{' '}
                {isEmail
                  ? <span className="font-medium break-all">{raw}</span>
                  : <span className="font-medium">‘{raw}’</span>}
              </span>
              <span className="block text-[10px] themed-text-muted">This is a new connection</span>
            </span>
          </button>
        </div>
      )}
    </div>
  )
}
